import { connect } from 'react-redux';
import { defineMessages } from 'react-intl';
import get from 'lodash.get';

// Store and actions
import Global from '../../store/global-selectors';
import { printCustomsForms, printLabel, printPackingSlip } from '../../store/printing/print.actions';
import * as SendNotificationsActions from '../../store/send-notifications/send-notifications.actions';
import { undoMarkShipped } from '../../store/mark-as-shipped/mark-as-shipped.actions';

import { copyToClipboard } from '../../services/copy-to-clipboard';
import { FulfillmentPlanService } from '../../services/fulfillment-plan-service';

// Components
import ShipmentObject from '../../components/shipment-object';

import { FulfillmentPlanFulfillmentType } from '../../enums/fulfillment-plan-fulfillment-type';

const messages = defineMessages({
  printPackingSlip: {
    description: 'shipmentObjectContainer.printPackingSlip',
    defaultMessage: 'Print Packing Slip',
    id: 'XD6T6J',
  },
  printLabel: {
    description: 'shipmentObjectContainer.printLabel',
    defaultMessage: 'Print Label',
    id: 'MlcxCz',
  },
  undoMarkShipped: {
    description: 'shipmentObjectContainer.undoMarkShipped',
    defaultMessage: 'Undo "Mark As Shipped"',
    id: 'Zb4byx',
  },
  sendNotifications: {
    description: 'shipmentObjectContainer.sendNotifications',
    defaultMessage: 'Send Notifications',
    id: 'zSSGYL',
  },
  retryNotifications: {
    description: 'shipmentObjectContainer.retryNotifications',
    defaultMessage: 'Retry Notifications',
    id: 'hvlfbS',
  },
  printCustomsForms: {
    description: 'shipmentObjectContainer.printCustomsForms',
    defaultMessage: 'Print Customs Forms',
    id: 'GHsB+R',
  },
  copyTrackingNumber: {
    description: 'shipmentObjectContainer.copyTrackingNumber',
    defaultMessage: 'Copy Tracking Number',
    id: 'Sb8IR2',
  },
  assignShipment: {
    description: 'shipmentObjectContainer.assignShipment',
    defaultMessage: 'Assign Shipment',
    id: '7iYs4q',
  },
});

const userCanInteractWithFulfillments = (hasShippingRestriction, currentUserId, fulfillmentPlan) =>
  !(hasShippingRestriction && currentUserId !== get(fulfillmentPlan, 'assignedToUserId'));

const mapStatesToProps = (state, props) => {
  const { shipment, isExpandedByDefault, fulfillments, otherSalesOrdersFulfilled } = props;

  const isExternal = FulfillmentPlanService.isExternal(shipment); // I know this shipment isn't exactly a fulfillmentPlan, but the shipment should be mape from a fulfillmentPlan
  let shipFrom = get(Global.selectSellerShipFromsById(state, shipment), 'name');
  if (isExternal) {
    const sellerFulfillmentProviderId = get(shipment, 'externalFulfillmentConfiguration.sellerFulfillmentProviderId');
    const fulfillmentProviderData = Global.selectSellerActiveFulfillmentProviders(state).find(
      (fulfillmentProvider) => fulfillmentProvider.sellerFulfillmentProviderId === sellerFulfillmentProviderId,
    );
    shipFrom = get(fulfillmentProviderData, 'name');
  }
  const packageType = get(Global.selectSellerPackageType(state, shipment), 'name');
  const currentUserId = Global.selectUserCurrentUserId(state);
  const hasShippingRestriction = Global.selectUseruserHasShipmentViewRestriction(state);
  const shouldShowShippingLink = userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment);
  const hasShippingRole = Global.selectUserhasShippingRole(state);
  return {
    shipment,
    otherSalesOrdersFulfilled,
    isExpandedByDefault,
    shipFrom,
    packageType,
    fulfillments,
    currentUserId,
    hasShippingRestriction,
    shouldShowShippingLink,
    hasShippingRole,
  };
};

const mapDispatchToProps = (dispatch) => {
  const actions = [];

  actions.push({
    translation: messages.printPackingSlip,
    handler: ({ shipment }) => {
      const { fulfillmentPlanId } = shipment;
      const isPackingSlipPrinted = get(shipment, 'packingSlipPrintedStatus.isPrinted', false);

      // todo maybe auto-print?
      dispatch(printPackingSlip([fulfillmentPlanId], isPackingSlipPrinted));
    },
  });

  actions.push({
    translation: messages.printLabel,
    handler: ({ shipment, fulfillments }) => {
      const { fulfillmentPlanId } = shipment;
      const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
      if (!nonVoidedFulfillment) {
        return;
      }
      const isLabelPrinted = get(nonVoidedFulfillment, 'labelPrintedStatus.isPrinted', false);
      const { fulfillmentId } = nonVoidedFulfillment;

      // todo maybe auto-print?
      dispatch(printLabel([fulfillmentPlanId], [fulfillmentId], isLabelPrinted));
    },
    shouldBeDisabled: ({ fulfillments, currentUserId, shipment, hasShippingRestriction, hasShippingRole }) => {
      const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
      return !userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment) || !hasShippingRole || !nonVoidedFulfillment;
    },
  });

  actions.push({
    translation: messages.undoMarkShipped,
    handler: ({ shipment }) => {
      const fulfillmentPlanId = get(shipment, 'fulfillmentPlanId');
      dispatch(undoMarkShipped([fulfillmentPlanId]));
    },
    shouldBeRemoved: ({ shipment }) => {
      return get(shipment, 'fulfillmentType') !== FulfillmentPlanFulfillmentType.Manual;
    },
    shouldBeDisabled: ({ currentUserId, shipment, hasShippingRestriction, hasShippingRole }) => {
      return !userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment) || !hasShippingRole;
    },
  });

  const notificationProperties = ['marketplaceNotification', 'shipmentEmailNotification', 'deliveryEmailNotification'];

  actions.push({
    getTranslation: ({ shipment }) => {
      const notificationsHasErrors = notificationProperties.some((propertyName) => get(shipment, `${propertyName}.errorMessage`));
      return notificationsHasErrors ? messages.retryNotifications : messages.sendNotifications;
    },
    handler: ({ shipment }) => {
      const { fulfillmentPlanId } = shipment;
      dispatch(SendNotificationsActions.sendNotifications([fulfillmentPlanId]));
    },
    shouldBeDisabled: ({ fulfillments, currentUserId, shipment, hasShippingRestriction, hasShippingRole }) =>
      !userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment) || !hasShippingRole || !fulfillments.length,
  });

  actions.push({
    translation: messages.printCustomsForms,
    handler: ({ shipment }) => {
      const { fulfillmentPlanId } = shipment;
      dispatch(printCustomsForms([fulfillmentPlanId]));
    },
    shouldBeRemoved: ({ shipment, fulfillments }) => {
      if (!shipment) {
        return true;
      }
      const needsCustoms = get(shipment, 'properties.needsCustoms');
      if (!needsCustoms) {
        return true;
      }
      const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
      const hasFormFile = get(nonVoidedFulfillment, 'properties.hasFormFile');
      return !hasFormFile;
    },
    shouldBeDisabled: ({ currentUserId, shipment, hasShippingRestriction, hasShippingRole }) => {
      return !userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment) || !hasShippingRole;
    },
  });

  actions.push({
    translation: messages.copyTrackingNumber,
    handler: ({ fulfillments }) => {
      const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
      const trackingNumber = get(nonVoidedFulfillment, 'trackingInformation.trackingNumber');
      copyToClipboard(trackingNumber);
    },
    shouldBeDisabled: ({ fulfillments, currentUserId, shipment, hasShippingRestriction, hasShippingRole }) => {
      const nonVoidedFulfillment = fulfillments.find((fulfillment) => !fulfillment.isLabelVoided);
      return !userCanInteractWithFulfillments(hasShippingRestriction, currentUserId, shipment) || !hasShippingRole || !nonVoidedFulfillment;
    },
  });

  return {
    actions,
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const combinedProps = {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
  };
  const actions = combinedProps.actions
    .filter((action) => !action.shouldBeRemoved || !action.shouldBeRemoved(combinedProps))
    .map(({ translation, handler, shouldBeDisabled, getTranslation }) => ({
      translation: getTranslation ? getTranslation(combinedProps) : translation,
      handler,
      disabled: shouldBeDisabled && shouldBeDisabled(combinedProps),
    }));
  return {
    ...combinedProps,
    actions,
  };
};

export const ShipmentObjectContainer = connect(mapStatesToProps, mapDispatchToProps, mergeProps)(ShipmentObject);
